import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Select,
    Spin,
    Table,
    Tabs,
    Tag,
    Space,
    Typography
} from 'antd';
import {
    CalculatorOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SettingOutlined,
    EditOutlined
} from '@ant-design/icons';
import { Box, Grid } from '@mui/material';
import type { TabsProps } from 'antd';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import { apiEndpoints } from '../../../config';
import { Crypter } from '../../../Functions/Security/Crypter';
import { normalizePalier, TarificationGrille, TarificationPalier } from '../../../Models/Tarification';
import Utilisateur from '../../../Models/Utilisateur';
import formatNumber from '../../../Functions/Traitement/FormatNumber';

const VERT = '#116599';

const TYPES_PRELEVEMENT = [
    { value: 'POURCENTAGE', label: 'Pourcentage (%)' },
    { value: 'FIXE', label: 'Montant fixe' },
    { value: 'MIXTE', label: 'Mixte (% + fixe)' }
];

/**
 * Gestion des grilles de tarification (paliers).
 * Chaque palier definit le type de prelevement (pourcentage, fixe ou mixte)
 * avec le minimum et le maximum prelevable.
 * Appels : POST /api/Tarification/Gerer (action dans le payload chiffre).
 */
const TarificationManage = () => {
    const authHeader = useAuthHeader();
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const GERER_URL = `${apiEndpoints.tarification}/Gerer`;
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [grilles, setGrilles] = useState<TarificationGrille[]>([]);
    const [selectedCodTarif, setSelectedCodTarif] = useState<string | null>(null);
    const [paliers, setPaliers] = useState<TarificationPalier[]>([]);
    const [dirty, setDirty] = useState(false);

    // États CRUD standard
    const [ajoutActve, setAjoutActif] = useState(false);
    const [modifActve, setModifActve] = useState(true);
    const [enregActve, setEnregActve] = useState(true);
    const [supActive, setSupActive] = useState(true);
    const [actuActive, setActActive] = useState(false);
    const [annulActive, setAnnulActive] = useState(true);
    const [mode, setMode] = useState<'create' | 'update' | 'none'>('none');

    // Test de calcul
    const [montantTest, setMontantTest] = useState<number | null>(null);
    const [fraisTest, setFraisTest] = useState<{ frais: number; numPalier: number } | null>(null);

    const appelApi = async (payload: any): Promise<any> => {
        const cypher = Crypter.encrypt(JSON.stringify(payload));
        const res = await axios.post(GERER_URL, JSON.stringify(cypher), {
            headers: { Authorization: authHeader(), 'Content-Type': 'application/json' }
        });
        const raw = JSON.parse(Crypter.decrypt(res.data));
        return raw.Value ?? raw;
    };

    const chargerGrilles = async () => {
        setLoading(true);
        try {
            const res = await appelApi({ Action: 'GRILLES', Utilisateur: user?.coduti });
            const list = res?.Result?.Table ?? [];
            setGrilles(list.map((g: any) => ({
                codTarif: String(g.codTarif ?? g.CODTARIF ?? ''),
                nbPaliers: Number(g.nbPaliers ?? g.NBPALIERS ?? 0),
                libelleGrille: String(g.libelleGrille ?? g.LIBELLE_GRILLE ?? '')
            })));
        } catch (e: any) {
            SweetAlert.fire({ title: 'Erreur', text: String(e.message ?? e), icon: 'error' });
        }
        setLoading(false);
    };

    const chargerPaliers = async (code: string) => {
        if (!code) return;
        setLoading(true);
        setFraisTest(null);
        try {
            const res = await appelApi({ Action: 'LISTE', CodTarif: code });
            const list = res?.Result?.Table ?? [];
            setPaliers(list.map(normalizePalier));
            setDirty(false);
        } catch (e: any) {
            SweetAlert.fire({ title: 'Erreur', text: String(e.message ?? e), icon: 'error' });
        }
        setLoading(false);
    };

    useEffect(() => { chargerGrilles(); }, []);

    // Fonctions CRUD standard
    function initialiazeButton() {
        setAjoutActif(false);
        setModifActve(true);
        setEnregActve(true);
        setSupActive(true);
        setActActive(false);
        setAnnulActive(true);
    }

    const SetStart = () => {
        setMode('create');
        form.resetFields();
        setSelectedCodTarif(null);
        setPaliers([]);
        setDirty(false);
        setFraisTest(null);
        setAjoutActif(true);
        setEnregActve(false);
        setModifActve(true);
        setSupActive(true);
        setAnnulActive(false);
        setActActive(true);
    };

    const modif = () => {
        if (!selectedCodTarif) {
            SweetAlert.fire({ title: 'Attention', text: 'Veuillez sélectionner une grille', icon: 'warning' });
            return;
        }
        setMode('update');
        setAjoutActif(true);
        setEnregActve(false);
        setModifActve(true);
        setSupActive(true);
        setAnnulActive(false);
        setActActive(true);
    };

    const SetEndStart = () => {
        if (!selectedCodTarif) return;
        SweetAlert.fire({
            title: 'Suppression',
            text: `Voulez-vous supprimer la grille ${selectedCodTarif} ?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#116599',
            cancelButtonColor: 'darkred',
            confirmButtonText: 'Oui'
        }).then((r) => {
            if (r.isConfirmed) supprimerGrille();
        });
    };

    function handleAnulle() {
        form.resetFields();
        setSelectedCodTarif(null);
        setPaliers([]);
        setDirty(false);
        setFraisTest(null);
        setMode('none');
        initialiazeButton();
        chargerGrilles();
    }

    const ajouterPalier = () => {
        const codTarif = form.getFieldValue('CODTARIF');
        const libelleGrille = form.getFieldValue('LIBELLE');
        if (!codTarif?.trim()) {
            SweetAlert.fire({ title: 'Code grille requis', text: 'Renseignez le code de la grille avant d\'ajouter des paliers.', icon: 'warning' });
            return;
        }
        const nextNum = paliers.length ? Math.max(...paliers.map(p => p.numPalier)) + 1 : 1;
        // Borne sup incluse : le palier suivant commence APRES la borne sup
        // precedente (ex : palier 0-1000 -> prochain palier a 1001).
        const dernier = paliers.length ? paliers[paliers.length - 1] : null;
        if (dernier && dernier.borneSup === null) {
            SweetAlert.fire({ title: 'Borne supérieure requise', text: 'Le dernier palier n\'a pas de borne supérieure : renseignez-la avant d\'ajouter un palier suivant.', icon: 'warning' });
            return;
        }
        const borneInf = dernier ? Number(dernier.borneSup) + 1 : 0;
        setPaliers([...paliers, {
            codTarif: codTarif.trim().toUpperCase(),
            numPalier: nextNum,
            borneInf,
            borneSup: null,
            typPrelev: 'POURCENTAGE',
            taux: 0,
            mtnFixe: null,
            mtnMin: null,
            mtnMax: null,
            libelleGrille: libelleGrille?.trim() ?? ''
        }]);
        setDirty(true);
    };

    const updatePalier = (index: number, field: keyof TarificationPalier, value: any) => {
        const next = [...paliers];
        next[index] = { ...next[index], [field]: value };
        setPaliers(next);
        setDirty(true);
    };

    const retirerPalier = (index: number) => {
        setPaliers(paliers.filter((_, i) => i !== index));
        setDirty(true);
    };

    const validerPaliers = (): string | null => {
        for (const p of paliers) {
            if (p.typPrelev !== 'FIXE' && p.taux === null) return `Palier ${p.numPalier} : taux requis (${p.typPrelev}).`;
            if (p.typPrelev !== 'POURCENTAGE' && p.mtnFixe === null) return `Palier ${p.numPalier} : montant fixe requis (${p.typPrelev}).`;
            if (p.borneSup !== null && p.borneSup <= p.borneInf) return `Palier ${p.numPalier} : borne sup doit etre > borne inf.`;
            if (p.mtnMin !== null && p.mtnMax !== null && p.mtnMin > p.mtnMax) return `Palier ${p.numPalier} : min > max.`;
        }
        // Borne sup incluse : aucun chevauchement possible entre paliers.
        const tries = [...paliers].sort((a, b) => a.borneInf - b.borneInf);
        for (let i = 1; i < tries.length; i++) {
            const prev = tries[i - 1], cur = tries[i];
            if (prev.borneSup !== null && cur.borneInf <= prev.borneSup) {
                return `Les paliers ${prev.numPalier} et ${cur.numPalier} se chevauchent : la borne inf de ${cur.numPalier} doit être supérieure à ${prev.borneSup} (ex. ${Number(prev.borneSup) + 1}).`;
            }
        }
        return null;
    };

    const onFinish = async (values: any) => {
        const erreur = validerPaliers();
        if (erreur) {
            SweetAlert.fire({ title: 'Validation', text: erreur, icon: 'warning' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                Action: 'ECRASER',
                Utilisateur: user?.coduti,
                Items: paliers.map(p => ({ ...p, codTarif: values.CODTARIF.trim().toUpperCase(), libelleGrille: values.LIBELLE?.trim() ?? '' }))
            };
            const res = await appelApi(payload);
            if (res?.StatutCode === 200) {
                SweetAlert.fire({ title: 'Succes', text: res.Message, icon: 'success' });
                setDirty(false);
                chargerGrilles();
                if (mode === 'create') {
                    setSelectedCodTarif(values.CODTARIF.trim().toUpperCase());
                    chargerPaliers(values.CODTARIF.trim().toUpperCase());
                }
                handleAnulle();
            } else {
                SweetAlert.fire({ title: 'Erreur', text: res?.Message ?? 'Echec', icon: 'error' });
            }
        } catch (e: any) {
            SweetAlert.fire({ title: 'Erreur', text: String(e.message ?? e), icon: 'error' });
        }
        setLoading(false);
    };

    const supprimerGrille = async () => {
        if (!selectedCodTarif) return;
        setLoading(true);
        try {
            const res = await appelApi({ Action: 'SUPPRIMER', CodTarif: selectedCodTarif });
            if (res?.StatutCode === 200) {
                SweetAlert.fire({ title: 'Succes', text: res.Message, icon: 'success' });
                handleAnulle();
                chargerGrilles();
            } else {
                SweetAlert.fire({ title: 'Erreur', text: res?.Message ?? 'Echec', icon: 'error' });
            }
        } catch (e: any) {
            SweetAlert.fire({ title: 'Erreur', text: String(e.message ?? e), icon: 'error' });
        }
        setLoading(false);
    };

    // Calcul local sur les paliers affiches (meme non enregistres) :
    // meme logique que sp_AnyManageTarification 'CALCULER'
    // (borne superieure incluse : borneInf <= montant <= borneSup).
    const calculerFrais = () => {
        if (montantTest === null) {
            SweetAlert.fire({ title: 'Montant requis', icon: 'warning' });
            return;
        }
        const palier = paliers
            .filter(p => p.borneInf <= montantTest && (p.borneSup === null || montantTest <= p.borneSup))
            .sort((a, b) => b.borneInf - a.borneInf)[0];
        if (!palier) {
            setFraisTest(null);
            SweetAlert.fire({
                title: 'Aucun palier',
                text: `Aucun palier ne couvre le montant ${formatNumber(montantTest)}.`,
                icon: 'warning'
            });
            return;
        }
        let frais = (palier.mtnFixe ?? 0) + (palier.taux ?? 0) * montantTest / 100;
        if (palier.mtnMin !== null && frais < palier.mtnMin) frais = palier.mtnMin;
        if (palier.mtnMax !== null && frais > palier.mtnMax) frais = palier.mtnMax;
        setFraisTest({ frais, numPalier: palier.numPalier });
    };

    const colonnesGrilles = [
        { title: 'Code grille', dataIndex: 'codTarif', key: 'codTarif' },
        { title: 'Libellé', dataIndex: 'libelleGrille', key: 'libelleGrille' },
        { title: 'Nb paliers', dataIndex: 'nbPaliers', key: 'nbPaliers' }
    ];

    const colonnesPaliers = useMemo(() => [
        { title: 'N°', dataIndex: 'numPalier', width: 60 },
        {
            title: 'Borne inf.', width: 120,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    value={paliers[i]?.borneInf}
                    onChange={(v) => updatePalier(i, 'borneInf', v ?? 0)} />
            )
        },
        {
            title: 'Borne sup. (incluse)', width: 140,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    placeholder='Sans plafond'
                    value={paliers[i]?.borneSup}
                    onChange={(v) => updatePalier(i, 'borneSup', v)} />
            )
        },
        {
            title: 'Type', width: 160,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <Select size='small' style={{ width: '100%' }}
                    value={paliers[i]?.typPrelev}
                    options={TYPES_PRELEVEMENT}
                    onChange={(v) => updatePalier(i, 'typPrelev', v)} />
            )
        },
        {
            title: 'Taux %', width: 100,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    disabled={paliers[i]?.typPrelev === 'FIXE'}
                    value={paliers[i]?.taux}
                    onChange={(v) => updatePalier(i, 'taux', v)} />
            )
        },
        {
            title: 'Montant fixe', width: 110,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    disabled={paliers[i]?.typPrelev === 'POURCENTAGE'}
                    value={paliers[i]?.mtnFixe}
                    onChange={(v) => updatePalier(i, 'mtnFixe', v)} />
            )
        },
        {
            title: 'Min prélevé', width: 110,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    value={paliers[i]?.mtnMin}
                    onChange={(v) => updatePalier(i, 'mtnMin', v)} />
            )
        },
        {
            title: 'Max prélevé', width: 110,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <InputNumber size='small' style={{ width: '100%' }} min={0}
                    value={paliers[i]?.mtnMax}
                    onChange={(v) => updatePalier(i, 'mtnMax', v)} />
            )
        },
        {
            title: '', width: 50,
            render: (_: any, _r: TarificationPalier, i: number) => (
                <Button danger size='small' icon={<DeleteOutlined />} onClick={() => retirerPalier(i)} />
            )
        }
    ], [paliers]);

    const onSelectGrille = (record: TarificationGrille) => {
        setSelectedCodTarif(record.codTarif);
        form.setFieldsValue({ CODTARIF: record.codTarif, LIBELLE: record.libelleGrille });
        chargerPaliers(record.codTarif);
        setModifActve(false);
        setSupActive(false);
        setMode('none');
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: (
                <span id='Tab'>
                    <SettingOutlined />
                    Grilles de tarification
                </span>
            ),
            children: (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <Box className='bordure1' style={{ height: 'calc(100vh - 156px)', overflow: 'auto', padding: 16 }}>
                            <Table
                                size='small'
                                columns={colonnesGrilles}
                                dataSource={grilles}
                                rowKey={(r) => r.codTarif}
                                pagination={false}
                                onRow={(record) => ({
                                    onClick: () => onSelectGrille(record),
                                    style: { cursor: 'pointer', backgroundColor: selectedCodTarif === record.codTarif ? '#e6f7ff' : undefined }
                                })}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Box className='bordure1' style={{ height: 'calc(100vh - 156px)', overflow: 'auto', padding: 16 }}>
                            <Form.Item
                                label="Code grille"
                                name="CODTARIF"
                                rules={[{ required: true, message: 'Code requis' }]}
                            >
                                <Input
                                    placeholder="Ex: VIREMENT"
                                    disabled={mode === 'none'}
                                    onChange={() => setDirty(true)}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Libellé"
                                name="LIBELLE"
                            >
                                <Input
                                    placeholder="Description de la grille"
                                    disabled={mode === 'none'}
                                    onChange={() => setDirty(true)}
                                />
                            </Form.Item>

                            <Box style={{ marginTop: 16, marginBottom: 8 }}>
                                <Typography.Text strong style={{ fontSize: 14 }}>
                                    Paliers {dirty && <Tag color='orange'>non enregistré</Tag>}
                                </Typography.Text>
                                <Typography.Text type='secondary' style={{ fontSize: 12, marginLeft: 8 }}>
                                    (borne sup incluse : 0-1000 → palier suivant à 1001)
                                </Typography.Text>
                            </Box>
                            <Box style={{ marginBottom: 8 }}>
                                <Button size='small' icon={<PlusOutlined />} onClick={ajouterPalier} disabled={mode === 'none'}>
                                    Ajouter un palier
                                </Button>
                            </Box>
                            {paliers.length === 0 ? (
                                <Box style={{ textAlign: 'center', color: '#999', marginTop: 16 }}>
                                    Aucun palier — sélectionnez une grille ou créez-en une nouvelle
                                </Box>
                            ) : (
                                <Table
                                    size='small'
                                    columns={colonnesPaliers as any}
                                    dataSource={paliers}
                                    rowKey={(r) => `${r.numPalier}`}
                                    pagination={false}
                                    scroll={{ x: 'max-content' }}
                                />
                            )}

                            <Box style={{ marginTop: 24, marginBottom: 8 }}>
                                <Typography.Text strong style={{ fontSize: 14 }}>
                                    <CalculatorOutlined /> Tester le calcul
                                </Typography.Text>
                                <Typography.Text type='secondary' style={{ fontSize: 12, marginLeft: 8 }}>
                                    (sur les paliers affichés, même non enregistrés)
                                </Typography.Text>
                            </Box>
                            <Box style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <InputNumber
                                    size='small'
                                    min={0}
                                    disabled={false}
                                    style={{ width: 200 }}
                                    placeholder='Montant à tester'
                                    value={montantTest}
                                    onChange={(v) => setMontantTest(v)}
                                />
                                <Button size='small' icon={<CalculatorOutlined />} onClick={calculerFrais}
                                    disabled={montantTest === null || paliers.length === 0}>
                                    Calculer
                                </Button>
                                {fraisTest && (
                                    <Tag color='#116599' style={{ fontSize: 14, padding: '4px 10px' }}>
                                        Frais : {formatNumber(fraisTest.frais)} (palier {fraisTest.numPalier})
                                    </Tag>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            )
        }
    ];

    return (
        <div className='tab'>
            <Typography className='formTitle'><b>GESTION DES GRILLES DE TARIFICATION</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form disabled={!ajoutActve} autoComplete='off' labelAlign='left' form={form} name='tarification_form' onFinish={onFinish} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
                    <div className='bG'>
                        <legend><Typography color={'#116599'}>Actions</Typography></legend>
                        <Form.Item shouldUpdate className='bouttong'>
                            <Space direction='vertical' style={{ width: '100%' }}>
                                <Button size='large' className='bt_add' disabled={ajoutActve} onClick={SetStart} type='ghost' htmlType='button' block>Ajouter</Button>
                                <Button size='large' className='bt_add' disabled={modifActve} onClick={modif} type='ghost' htmlType='button' block>Modifier</Button>
                                <Button size='large' className='bt_add' disabled={enregActve} type='ghost' htmlType='submit' block>Enregistrer</Button>
                                <Button size='large' className='bt_add' onClick={SetEndStart} disabled={supActive} type='ghost' htmlType='button' block>Supprimer</Button>
                                <Button size='large' disabled={actuActive} onClick={chargerGrilles} className='bt_add' type='ghost' htmlType='button' block>Actualiser</Button>
                                <Button size='large' disabled={annulActive} onClick={handleAnulle} className='bt_add' type='ghost' htmlType='reset' block>Annuler</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP' style={{width: "88%"}}>
                        <div>
                            <Tabs size='large' type='card' defaultActiveKey='1' items={items} />
                        </div>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default TarificationManage;
